/**
 * PlayerColumn — everything the reviewer sees above the metadata strip: the
 * theater (stage + transport), the annotate bar that replaces it while a frame
 * is being marked up, and the shortcut legend.
 *
 * The panel renders this instead of `MediaTheater` in anchored-notes mode so
 * the three stay in step with one another; the metadata strip and `leftExtras`
 * keep sitting below it, untouched.
 */
import { useCallback } from 'react';

import { AnnotateBar } from '../capture/AnnotateBar';
import { AnnotateStage } from '../capture/AnnotateStage';
import { MediaTheater, type CorsMode } from '../media/MediaTheater';
import { VideoLoadError } from '../media/VideoLoadError';
import type { MediaController } from '../media/useMediaController';
import type { NotesSession } from '../session/useNotesSession';
import { KeyHints } from './KeyHints';

export interface PlayerColumnProps {
  session: NotesSession;
  controller: MediaController;
  src: string;
  /** The video failed for good: an error with a retry covers the stage. */
  loadFailed: boolean;
  onRetryLoad: () => void;
  title: string;
  seekFlashNonce: number;
  corsMode: CorsMode;
}

export function PlayerColumn({ session, controller, src, loadFailed, onRetryLoad, title, seekFlashNonce, corsMode }: PlayerColumnProps) {
  const { capture } = session;

  // `attachFrame` bakes, so it is async; the click handler does not wait on it
  // — `session.baking` is what the bar renders while it runs.
  const attachFrame = useCallback(() => {
    void session.attachFrame();
  }, [session]);

  // The stage lies over the still-mounted <video>: discarding must put the
  // reviewer back on the very same playhead, so the element is never replaced.
  function stageOverlay() {
    if (capture) {
      return (
        <AnnotateStage bitmap={capture.bitmap} width={capture.width} height={capture.height} state={session.annotation} dispatch={session.dispatchAnnotation} busy={session.baking} />
      );
    }
    if (loadFailed) return <VideoLoadError onRetry={onRetryLoad} />;
    return null;
  }

  return (
    <div className="flex flex-col" data-testid="player-column">
      <MediaTheater
        controller={controller}
        session={session}
        src={src}
        title={title}
        seekFlashNonce={seekFlashNonce}
        overlay={stageOverlay()}
        corsMode={corsMode}
      />
      {capture ? (
        <AnnotateBar
          state={session.annotation}
          dispatch={session.dispatchAnnotation}
          tMs={capture.tMs}
          paletteOpen={session.paletteOpen}
          setPaletteOpen={session.setPaletteOpen}
          onAttach={attachFrame}
          onDiscard={session.discardCapture}
          busy={session.baking}
        />
      ) : null}
      <KeyHints hidden={!!capture} />
    </div>
  );
}
